import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Auth flow supports two providers running side-by-side:
 *   1. Password login (existing) — issues a JWT stored in localStorage.
 *   2. Emergent-managed Google Auth (new) — issues an httpOnly session_token
 *      cookie server-side. localStorage is not touched for this flow.
 * `/api/auth/me` accepts either credential and returns the same user shape.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Google Auth callback lands with #session_id=... in the fragment. We
    // read it SYNCHRONOUSLY (before any react-router hook can strip it) via
    // window.location.hash and exchange it for a session cookie.
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (sessionId) {
      // Clean the fragment so a page refresh doesn't re-post the session_id.
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, "", cleanUrl);
      api.post("/auth/google-session", { session_id: sessionId })
        .then(() => api.get("/auth/me"))
        .then((r) => setUser(r.data))
        .catch((e) => {
          const detail = e?.response?.data?.detail;
          setUser({ __error: typeof detail === "string" ? detail : "Google sign-in failed" });
          window.location.href = "/login?e=" + encodeURIComponent(detail || "Google sign-in failed");
        })
        .finally(() => setLoading(false));
      return;
    }
    // Normal boot — try /me. Works for both JWT (via localStorage token +
    // axios interceptor) and cookie session (via withCredentials).
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async () => {
    // Password login removed (Iter 27.1). Kept as a no-op stub so any lingering
    // references throw a clear error instead of silently failing.
    throw new Error("Password login is disabled — use Google sign-in.");
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("il_token");
    setUser(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
